import type { TFunction } from 'i18next';
import { z } from 'zod';

import {
  CountryCode,
  isAllowedVideoUrl,
  normalizeSignupPhoneToE164,
  VALIDATION_LIMITS,
  type ValidationMessage,
  ValidationMessages,
  VIMEO_REG_EXP,
} from '@repo/common';
import {
  CategoryType,
  ContactTopic,
  ContentAccessTier,
  DiscountDealRelationship,
  EmailTemplateType,
  EventStatus,
  WorkflowStatus,
} from '@repo/frontend-shared';
import type { FileData } from '@repo/ui/components/elements/FileInput/FileInput';

export enum CustomInstructionStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum AIInstructionScope {
  GLOBAL = 'GLOBAL',
  ALL_COURSES = 'ALL_COURSES',
  ALL_DOCUMENTS = 'ALL_DOCUMENTS',
  ALL_LESSONS = 'ALL_LESSONS',
  ALL_LIVE_EVENTS = 'ALL_LIVE_EVENTS',
  COURSE = 'COURSE',
  DOCUMENT = 'DOCUMENT',
  LESSON = 'LESSON',
}

const fieldMessageMap: Record<'subject' | 'body', ValidationMessage> = {
  subject: ValidationMessages.SUBJECT_REQUIRED,
  body: ValidationMessages.BODY_REQUIRED,
};

const requiredString = (message: ValidationMessage) => {
  return z.string({ message }).min(1, message);
};

const passwordSchema = requiredString(ValidationMessages.PASSWORD_REQUIRED)
  .min(12, ValidationMessages.PASSWORD_TOO_SHORT)
  .regex(/[a-zA-Z]/, ValidationMessages.PASSWORD_MISSING_LETTER)
  .regex(/\d/, ValidationMessages.PASSWORD_MISSING_NUMBER)
  .regex(/[^a-zA-Z\d]/, ValidationMessages.PASSWORD_MISSING_SPECIAL_SYMBOL);

const emailSchema = requiredString(ValidationMessages.EMAIL_REQUIRED).pipe(
  z.email(ValidationMessages.EMAIL_INVALID_ADDRESS),
);

const signupPhoneSchema = requiredString(ValidationMessages.PHONE_REQUIRED).refine(
  value => normalizeSignupPhoneToE164(value) !== undefined,
  ValidationMessages.PHONE_INVALID,
);

const titleSchema = requiredString(ValidationMessages.TITLE_REQUIRED);

const descriptionSchema = requiredString(ValidationMessages.DESCRIPTION_REQUIRED);

function normalizeBlankFormValue(value: unknown): unknown {
  return typeof value === 'string' && value.trim().length === 0 ? undefined : value;
}

const optionalFormStringSchema = z.preprocess(normalizeBlankFormValue, z.string().optional());
const optionalCountrySchema = z.preprocess(normalizeBlankFormValue, z.enum(CountryCode).nullish());

export const resolveOptionalTextUpdate = (
  value: string | undefined,
  previousValue: string | null | undefined,
): { value: string | undefined; shouldClear: boolean } => ({
  value,
  shouldClear: value === undefined && previousValue !== null && previousValue !== undefined,
});

const imageDataSchema = z
  .object({
    original: z.instanceof(File).optional(),
    thumbnail: z.instanceof(File).optional(),
    meta: z
      .object({
        x: z.number(),
        y: z.number(),
        aspect: z.number(),
        rotate: z.number(),
        scale: z.number(),
      })
      .optional(),
    url: z.string().nullish(),
    originalUrl: z.string().nullish(),
    originalId: z.string().optional(),
    thumbnailId: z.string().optional(),
  })
  .nullable();

export const optionalImageDataSchema = imageDataSchema.optional();

export const fileDataSchema: z.ZodType<FileData> = z.object({
  id: z.string().optional(),
  name: z.string(),
  url: z.string().optional(),
  file: z.instanceof(File).optional(),
});

const categorySchema = z.enum(CategoryType, {
  message: ValidationMessages.CATEGORY_REQUIRED,
});

const secondaryCategoriesSchema = z
  .array(categorySchema)
  .max(
    VALIDATION_LIMITS.CONTENT_SECONDARY_CATEGORIES_MAX,
    `Select up to ${VALIDATION_LIMITS.CONTENT_SECONDARY_CATEGORIES_MAX} secondary categories`,
  )
  .default([]);

const secondaryMentorsSchema = z.array(z.string()).default([]);

const withOptionalCategorySelectionValidation = <TShape extends z.ZodRawShape>(shape: TShape) =>
  z
    .object({
      ...shape,
      primaryCategory: categorySchema.optional(),
      secondaryCategories: secondaryCategoriesSchema,
    })
    .superRefine((data, ctx) => {
      const selection = data as {
        primaryCategory?: CategoryType;
        secondaryCategories: CategoryType[];
        mentor?: string;
        secondaryMentors?: string[];
      };
      if (new Set(selection.secondaryCategories).size !== selection.secondaryCategories.length) {
        ctx.addIssue({ code: 'custom', path: ['secondaryCategories'], message: 'Secondary categories must be unique' });
      }
      if (selection.primaryCategory && selection.secondaryCategories.includes(selection.primaryCategory)) {
        ctx.addIssue({
          code: 'custom',
          path: ['secondaryCategories'],
          message: 'Primary category cannot also be selected as secondary',
        });
      }
      if (!selection.primaryCategory && selection.secondaryCategories.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['primaryCategory'],
          message: 'Choose a primary category before secondary categories',
        });
      }
      // A primary mentor cannot also appear in secondary mentors.
      if (selection.secondaryMentors !== undefined) {
        if (new Set(selection.secondaryMentors).size !== selection.secondaryMentors.length) {
          ctx.addIssue({
            code: 'custom',
            path: ['secondaryMentors'],
            message: 'Secondary mentors must be unique',
          });
        }
        if (selection.mentor && selection.secondaryMentors.includes(selection.mentor)) {
          ctx.addIssue({
            code: 'custom',
            path: ['secondaryMentors'],
            message: 'Primary mentor cannot also be selected as secondary',
          });
        }
      }
    });

export const mentorSchema = z.object({
  name: requiredString(ValidationMessages.NAME_REQUIRED),
  email: emailSchema,
  position: requiredString(ValidationMessages.POSITION_REQUIRED),
  company: requiredString(ValidationMessages.COMPANY_REQUIRED),
  photoLink: z.url(ValidationMessages.INVALID_URL).min(1, ValidationMessages.PHOTO_LINK_REQUIRED),
  description: optionalFormStringSchema,
});

export const editMentorSchema = z.object({
  name: requiredString(ValidationMessages.NAME_REQUIRED),
  email: emailSchema,
  position: requiredString(ValidationMessages.POSITION_REQUIRED),
  company: requiredString(ValidationMessages.COMPANY_REQUIRED),
  description: z.string().optional(),
});

const passwordMatchSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine(data => data.password === data.confirmPassword, {
    message: ValidationMessages.PASSWORD_DO_NOT_MATCH,
    path: ['confirmPassword'],
  });

export const SignupSchema = z.object({
  email: emailSchema,
  firstName: requiredString(ValidationMessages.FIRST_NAME_REQUIRED),
  lastName: requiredString(ValidationMessages.LAST_NAME_REQUIRED),
  phone: signupPhoneSchema,
  password: passwordSchema,
});

export const passwordValidationSchema = passwordMatchSchema;

export const loginSchema = z.object({
  email: requiredString(ValidationMessages.EMAIL_REQUIRED).pipe(
    z.email({ message: ValidationMessages.EMAIL_INVALID_ADDRESS }),
  ),
  password: requiredString(ValidationMessages.PASSWORD_REQUIRED),
});

const EMAIL_LOCAL_PART_MAX = 64;

export const getInTouchSchema = z.object({
  firstName: requiredString(ValidationMessages.FIRST_NAME_REQUIRED)
    .trim()
    .min(1, ValidationMessages.FIRST_NAME_REQUIRED)
    .max(VALIDATION_LIMITS.FIRST_NAME_MAX, ValidationMessages.FIRST_NAME_TOO_LONG),
  lastName: requiredString(ValidationMessages.LAST_NAME_REQUIRED)
    .trim()
    .min(1, ValidationMessages.LAST_NAME_REQUIRED)
    .max(VALIDATION_LIMITS.LAST_NAME_MAX, ValidationMessages.LAST_NAME_TOO_LONG),
  email: requiredString(ValidationMessages.EMAIL_REQUIRED)
    .trim()
    .min(1, ValidationMessages.EMAIL_REQUIRED)
    .pipe(z.email({ message: ValidationMessages.EMAIL_INVALID_ADDRESS }))
    .pipe(z.string().max(VALIDATION_LIMITS.EMAIL_MAX, ValidationMessages.EMAIL_INVALID_ADDRESS))
    // The backend's `@IsEmail()` (validator.js) rejects local parts over 64 characters; zod does not.
    .refine(email => email.slice(0, email.lastIndexOf('@')).length <= EMAIL_LOCAL_PART_MAX, {
      message: ValidationMessages.EMAIL_INVALID_ADDRESS,
    }),
  // Starts as '' (no option chosen) and must end up a known topic.
  topic: requiredString(ValidationMessages.CONTACT_TOPIC_REQUIRED).pipe(
    z.enum(ContactTopic, { message: ValidationMessages.CONTACT_TOPIC_REQUIRED }),
  ),
  message: requiredString(ValidationMessages.CONTACT_MESSAGE_REQUIRED)
    .trim()
    .min(1, ValidationMessages.CONTACT_MESSAGE_REQUIRED)
    .max(VALIDATION_LIMITS.CONTACT_MESSAGE_MAX, ValidationMessages.CONTACT_MESSAGE_TOO_LONG),
});

export type GetInTouchFormValues = z.input<typeof getInTouchSchema>;
export type GetInTouchSubmission = z.output<typeof getInTouchSchema>;

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const courseSchema = withOptionalCategorySelectionValidation({
  title: titleSchema.max(100, 'Title cannot exceed 100 characters'),
  description: optionalFormStringSchema,
  picture: optionalImageDataSchema,
  previewVideo: z.array(fileDataSchema).max(1, 'Select only one preview video').optional(),
  mentor: optionalFormStringSchema,
  secondaryMentors: secondaryMentorsSchema,
  country: optionalCountrySchema,
});

export const lessonSchema = z.object({
  title: titleSchema,
  description: optionalFormStringSchema,
  contentId: requiredString(ValidationMessages.CONTENT_REQUIRED),
});

export const importVideoSchema = z.object({
  url: z.url().regex(VIMEO_REG_EXP),
});

export const editVideoSchema = z.object({
  title: titleSchema.min(3, 'Title must be at least 3 characters').max(200, 'Title cannot exceed 200 characters'),
  description: descriptionSchema.max(2000, 'Description cannot exceed 2000 characters'),
  url: z
    .string()
    .trim()
    .min(1, ValidationMessages.VIDEO_URL_REQUIRED)
    .refine(isAllowedVideoUrl, ValidationMessages.VIDEO_URL_INVALID),
});

export type EditVideoSchemaValues = z.infer<typeof editVideoSchema>;

type EmailTemplateContentItem = {
  subject: string;
  body: string;
  type: EmailTemplateType;
};

const normalizeEmailTemplateContentForValidation = (val: unknown): EmailTemplateContentItem[] => {
  const emptyItem = (type: EmailTemplateType): EmailTemplateContentItem => ({
    subject: '',
    body: '',
    type,
  });

  if (Array.isArray(val)) {
    return [
      {
        subject: val[0]?.subject ?? '',
        body: val[0]?.body ?? '',
        type: val[0]?.type ?? EmailTemplateType.EMAIL,
      },
      {
        subject: val[1]?.subject ?? '',
        body: val[1]?.body ?? '',
        type: val[1]?.type ?? EmailTemplateType.LINKEDIN,
      },
    ];
  }

  if (val && typeof val === 'object') {
    const record = val as Record<string, Partial<EmailTemplateContentItem> | undefined>;
    const email = record[0] ?? record['0'];
    const linkedin = record[1] ?? record['1'];

    return [
      {
        subject: email?.subject ?? '',
        body: email?.body ?? '',
        type: email?.type ?? EmailTemplateType.EMAIL,
      },
      {
        subject: linkedin?.subject ?? '',
        body: linkedin?.body ?? '',
        type: linkedin?.type ?? EmailTemplateType.LINKEDIN,
      },
    ];
  }

  return [emptyItem(EmailTemplateType.EMAIL), emptyItem(EmailTemplateType.LINKEDIN)];
};

const emailTemplateDraftSchema = withOptionalCategorySelectionValidation({
  title: titleSchema.max(100, 'Title cannot exceed 100 characters'),
  description: optionalFormStringSchema,
  picture: optionalImageDataSchema,
  mentor: optionalFormStringSchema,
  secondaryMentors: secondaryMentorsSchema,
  country: optionalCountrySchema,
  emailTemplateContent: z.preprocess(
    normalizeEmailTemplateContentForValidation,
    z
      .array(
        z.object({
          subject: z.preprocess((val: unknown) => (val === null || val === undefined ? '' : val), z.string()),
          body: z.preprocess((val: unknown) => (val === null || val === undefined ? '' : val), z.string()),
          type: z.enum(EmailTemplateType),
        }),
      )
      .length(2),
  ),
});

export const emailTemplateSchema = emailTemplateDraftSchema;

interface EmailChannelState {
  bodyPresent: boolean;
  complete: boolean;
  empty: boolean;
  subjectPresent: boolean;
}

function readEmailChannelState(channel: { body: string; subject: string }): EmailChannelState {
  const subjectPresent = channel.subject.trim().length > 0;
  const bodyPresent = channel.body.trim().length > 0;
  return {
    subjectPresent,
    bodyPresent,
    complete: subjectPresent && bodyPresent,
    empty: !(subjectPresent || bodyPresent),
  };
}

function addEmailChannelIssues(
  state: EmailChannelState,
  channelIndex: 0 | 1,
  required: boolean,
  ctx: z.RefinementCtx,
): void {
  if (state.complete || (state.empty && !required)) return;
  if (!state.subjectPresent) {
    ctx.addIssue({
      code: 'custom',
      path: ['emailTemplateContent', channelIndex, 'subject'],
      message: fieldMessageMap.subject,
    });
  }
  if (!state.bodyPresent) {
    ctx.addIssue({
      code: 'custom',
      path: ['emailTemplateContent', channelIndex, 'body'],
      message: fieldMessageMap.body,
    });
  }
}

function validatePublishableEmailTemplate(value: z.infer<typeof emailTemplateDraftSchema>, ctx: z.RefinementCtx): void {
  const email = readEmailChannelState(value.emailTemplateContent[0]);
  const linkedIn = readEmailChannelState(value.emailTemplateContent[1]);
  const channelRequired = !(email.complete || linkedIn.complete);
  addEmailChannelIssues(email, 0, channelRequired, ctx);
  addEmailChannelIssues(linkedIn, 1, channelRequired, ctx);
}

export const publishableEmailTemplateSchema = emailTemplateDraftSchema.superRefine(validatePublishableEmailTemplate);

const attachmentFilesSchema = z
  .array(
    z.object({
      id: z.string().optional(),
      name: z.string(),
      url: z.string().optional(),
      file: z.instanceof(File).optional(),
    }),
  )
  .optional();

const baseEventSchema = withOptionalCategorySelectionValidation({
  title: titleSchema.max(100, 'Title cannot exceed 100 characters'),
  description: optionalFormStringSchema,
  start: z.string().optional(),
  time: z.string().optional(),
  timezone: z.string().optional(),
  duration: z.string().optional(),
  mentor: optionalFormStringSchema,
  secondaryMentors: secondaryMentorsSchema,
  picture: optionalImageDataSchema,
  country: optionalCountrySchema,
  files: attachmentFilesSchema,
});

export const addEventSchema = baseEventSchema;

export const editEventSchema = baseEventSchema
  .extend({
    status: z.enum(EventStatus).optional(),
    eventLink: z.url(ValidationMessages.INVALID_URL).nullish().or(z.literal('')),
    recordingLink: z.url(ValidationMessages.INVALID_URL).nullish().or(z.literal('')),
    previewRecordingLink: z
      .string()
      .trim()
      .refine(value => value === '' || isAllowedVideoUrl(value), ValidationMessages.VIDEO_URL_INVALID)
      .nullish(),
    previewCutoffSeconds: z.coerce.number().int().positive().nullish().or(z.literal('')),
    approvePreviewRecording: z.boolean().optional(),
  })
  .superRefine((data, context) => {
    const hasPreviewLink = Boolean(data.previewRecordingLink);
    const hasPreviewCutoff = data.previewCutoffSeconds !== '' && data.previewCutoffSeconds != null;
    if (hasPreviewLink === hasPreviewCutoff) return;
    context.addIssue({
      code: 'custom',
      path: [hasPreviewLink ? 'previewCutoffSeconds' : 'previewRecordingLink'],
      message: 'Preview recording link and cutoff seconds must be provided together',
    });
  });

export const NameSchema = z.object({
  firstName: requiredString(ValidationMessages.FIRST_NAME_REQUIRED),
  lastName: requiredString(ValidationMessages.LAST_NAME_REQUIRED),
});

export const moduleSchema = z.object({
  title: titleSchema,
  description: optionalFormStringSchema,
});

export const documentTemplateSchema = withOptionalCategorySelectionValidation({
  title: titleSchema.max(100, 'Title cannot exceed 100 characters'),
  description: optionalFormStringSchema,
  videoId: z.string().optional(),
  mentor: optionalFormStringSchema,
  secondaryMentors: secondaryMentorsSchema,
  picture: optionalImageDataSchema,
  country: optionalCountrySchema,
  files: attachmentFilesSchema,
});

export const scenarioSchema = withOptionalCategorySelectionValidation({
  title: titleSchema.max(100, 'Title cannot exceed 100 characters'),
  description: z.preprocess(
    normalizeBlankFormValue,
    z.string().max(300, 'Description cannot exceed 300 characters').optional(),
  ),
  country: optionalCountrySchema,
});

const localizedRequiredString = (message: string) => z.string({ message }).min(1, message);

type DealRequiredTextField = 'description' | 'howToUse' | 'termsOfService' | 'value';

interface PublishableDealFormValues {
  description?: string;
  howToUse?: string;
  picture?: z.infer<typeof optionalImageDataSchema>;
  primaryCategory?: CategoryType;
  relationship?: DiscountDealRelationship;
  termsOfService?: string;
  value?: string;
  website?: string;
  workflowStatus: WorkflowStatus;
}

function addRequiredDealTextIssue(
  data: PublishableDealFormValues,
  field: DealRequiredTextField,
  message: string,
  ctx: z.RefinementCtx,
): void {
  if (!data[field]?.trim()) ctx.addIssue({ code: 'custom', path: [field], message });
}

function validateDealRequiredText(data: PublishableDealFormValues, ctx: z.RefinementCtx, t: TFunction<'admin'>): void {
  addRequiredDealTextIssue(data, 'description', t('deals.validation.descriptionRequired'), ctx);
  addRequiredDealTextIssue(data, 'howToUse', t('deals.validation.howToUseRequired'), ctx);
  addRequiredDealTextIssue(data, 'termsOfService', t('deals.validation.termsOfServiceRequired'), ctx);
  addRequiredDealTextIssue(data, 'value', t('deals.validation.valueRequired'), ctx);
}

function validateDealCategory(data: PublishableDealFormValues, ctx: z.RefinementCtx, t: TFunction<'admin'>): void {
  if (!data.primaryCategory) {
    ctx.addIssue({ code: 'custom', path: ['primaryCategory'], message: t('deals.validation.categoryRequired') });
  }
}

function validateDealWebsite(data: PublishableDealFormValues, ctx: z.RefinementCtx, t: TFunction<'admin'>): void {
  if (!data.website) {
    ctx.addIssue({ code: 'custom', path: ['website'], message: t('deals.validation.invalidUrl') });
  }
}

function validateDealPicture(data: PublishableDealFormValues, ctx: z.RefinementCtx): void {
  const hasPersistablePicture = Boolean(
    data.picture?.url || (data.picture?.original instanceof File && data.picture.thumbnail instanceof File),
  );
  if (!hasPersistablePicture) {
    ctx.addIssue({ code: 'custom', path: ['picture'], message: 'A logo is required before publishing' });
  }
}

function validateDealRelationship(data: PublishableDealFormValues, ctx: z.RefinementCtx): void {
  if (!data.relationship || data.relationship === DiscountDealRelationship.UNRESOLVED) {
    ctx.addIssue({ code: 'custom', path: ['relationship'], message: 'Relationship is required before publishing' });
  }
}

function validatePublishedDeal(data: PublishableDealFormValues, ctx: z.RefinementCtx, t: TFunction<'admin'>): void {
  if (data.workflowStatus !== WorkflowStatus.PUBLISHED) return;
  validateDealRequiredText(data, ctx, t);
  validateDealCategory(data, ctx, t);
  validateDealWebsite(data, ctx, t);
  validateDealPicture(data, ctx);
  validateDealRelationship(data, ctx);
}

export function createDealSchema(t: TFunction<'admin'>, requireRelationship = false) {
  const categorySchema = z.enum(CategoryType, {
    message: t('deals.validation.categoryRequired'),
  });

  const secondaryCategoriesSchema = z
    .array(categorySchema)
    .max(
      VALIDATION_LIMITS.CONTENT_SECONDARY_CATEGORIES_MAX,
      t('deals.validation.secondaryCategoriesMax', { max: VALIDATION_LIMITS.CONTENT_SECONDARY_CATEGORIES_MAX }),
    )
    .default([]);

  return z
    .object({
      title: localizedRequiredString(t('deals.validation.titleRequired')).max(
        VALIDATION_LIMITS.ADMIN_FORM_TITLE_MAX,
        t('deals.validation.titleTooLong', { max: VALIDATION_LIMITS.ADMIN_FORM_TITLE_MAX }),
      ),
      description: optionalFormStringSchema,
      picture: optionalImageDataSchema,
      howToUse: optionalFormStringSchema,
      termsOfService: optionalFormStringSchema,
      value: optionalFormStringSchema,
      website: z.preprocess(normalizeBlankFormValue, z.httpUrl(t('deals.validation.invalidUrl')).optional()),
      workflowStatus: z.enum(WorkflowStatus).default(WorkflowStatus.DRAFT),
      contentAccessTier: z.enum(ContentAccessTier).default(ContentAccessTier.PREMIUM),
      relationship: requireRelationship
        ? z
            .enum(DiscountDealRelationship, { message: 'Relationship is required' })
            .exclude([DiscountDealRelationship.UNRESOLVED])
        : z.enum(DiscountDealRelationship).exclude([DiscountDealRelationship.UNRESOLVED]).optional(),
      country: optionalCountrySchema,
      primaryCategory: categorySchema.optional(),
      secondaryCategories: secondaryCategoriesSchema,
    })
    .superRefine((data, ctx) => {
      const uniqueSecondaryCategories = new Set(data.secondaryCategories);

      if (uniqueSecondaryCategories.size !== data.secondaryCategories.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['secondaryCategories'],
          message: t('deals.validation.secondaryCategoriesUnique'),
        });
      }

      if (data.primaryCategory && data.secondaryCategories.includes(data.primaryCategory)) {
        ctx.addIssue({
          code: 'custom',
          path: ['secondaryCategories'],
          message: t('deals.validation.primaryCannotBeSecondary'),
        });
      }

      validatePublishedDeal(data, ctx, t);
    });
}

export type DealSchemaValues = z.infer<ReturnType<typeof createDealSchema>>;

export const categoryFormSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  imageUrl: z.url(ValidationMessages.INVALID_URL).optional(),
});

export const userProfileFormSchema = z.object({
  tagline: z.string().max(VALIDATION_LIMITS.TAGLINE_MAX, ValidationMessages.TAGLINE_TOO_LONG).optional(),
  timezone: z.string().optional(),
  firstName: z.string().min(1, ValidationMessages.FIRST_NAME_REQUIRED),
  lastName: z.string().min(1, ValidationMessages.LAST_NAME_REQUIRED),
  // phoneNumber: z.string().min(1, ValidationMessages.PHONE_REQUIRED),
  company: z.string().min(1, ValidationMessages.COMPANY_REQUIRED),
  position: z.string().min(1, ValidationMessages.POSITION_REQUIRED),
  description: z.string().min(20, ValidationMessages.ABOUT_ME_REQUIRED),
  email: z.email(ValidationMessages.EMAIL_INVALID_FORMAT).min(1, ValidationMessages.EMAIL_REQUIRED).toLowerCase(),
  linkedin: requiredString(ValidationMessages.LINKEDIN_PROFILE_REQUIRED),
  instagram: z.string().optional().nullable(),
  x: z.string().optional().nullable(),
  tiktok: z.string().optional().nullable(),
});

const accessControlSchema = z.object({
  allowUsers: z.array(z.string()).default([]),
  blockUsers: z.array(z.string()).default([]),
  allowRoles: z.array(z.string()).default([]),
  blockRoles: z.array(z.string()).default([]),
  allowGroups: z.array(z.string()).default([]),
  blockGroups: z.array(z.string()).default([]),
});

export const gatekeeperSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  rolloutPercentage: z.number().min(0).max(100).default(0),
  accessControl: accessControlSchema.optional(),
  workflowStatus: z.enum(WorkflowStatus).default(WorkflowStatus.DRAFT),
});

export const FeatureFlagSchema = z.object({
  title: titleSchema.max(100, 'Title cannot exceed 100 characters'),
  description: descriptionSchema,
  rolloutPercentage: z.number().min(0).max(100).default(0),
  accessControl: accessControlSchema.optional(),
  workflowStatus: z.enum(WorkflowStatus).default(WorkflowStatus.DRAFT),
});

export const customInstructionSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  content: requiredString(ValidationMessages.CONTENT_REQUIRED),
  context: z.string().optional(),
  status: z.enum(CustomInstructionStatus),
  tags: z.string(),
  isDefault: z.boolean(),
  assignedTo: z.string().optional(),
});

export const aiInstructionSchema = z.object({
  title: titleSchema,
  content: requiredString(ValidationMessages.CONTENT_REQUIRED),
  scope: z.enum(AIInstructionScope),
  scopeId: z.string().optional(),
  priority: z.coerce.number().min(0).default(0),
  isActive: z.boolean().default(true),
});
